import {
  DEFAULT_TEMPLATE,
  type VerifyRequest,
  type VerifyResponse,
  type VerifyStrategy,
} from '@smart-link/shared';
import { useCallback, useEffect, useRef, useState } from 'react';
import { getAdClickIds } from './ad-click-ids';
import { markVerifyDone } from './beacon';
import { redirectToTarget, willBreakOut } from './browser';
import { getApiBase, getAppVersion, slDbg } from './config';
import { getDeviceId } from './device-id';
import { flushEventsBeacon, getEvents, markAllEventsFlushed, trackEvent } from './events';
import { collectSignals, recordChallenge } from './fingerprint';
import { postTelemetryJson } from './http';
import { getLinkId, getProbeResult, startProbe } from './probe';
import { sessionId } from './session';

declare global {
  interface Window {
    /** Full public config injected into the HTML <head> at the edge for a flash-free first paint. */
    __TC_CONFIG?: PublicConfig;
    /** Signed edge token injected at the edge; returned on verify for stateless cross-checks. */
    __TC_EDGE?: string;
  }
}

export type CloakState =
  | { status: 'loading' }
  | { status: 'verifying' }
  | { status: 'awaiting-click'; decision?: 'pass' | 'block' }
  | { status: 'redirecting'; decision: 'pass' | 'block' }
  | { status: 'error'; message: string };

interface PublicConfig {
  requireChallenge: boolean;
  /** 链接级验证策略;缺失(旧注入)时由 requireChallenge 派生。 */
  verifyStrategy?: VerifyStrategy;
  challengeTimeoutMs: number;
  linkStatus?: 'active' | 'paused';
  template: string;
  appName: string;
  logo: string;
  buttonColor: string;
  /** true=跳转时留在应用内浏览器,不做 scheme breakout(下游回流页需要)。 */
  keepInAppBrowser?: boolean;
}

/** 派生生效策略:优先 verifyStrategy,回落到 requireChallenge(false=auto,true=challenge)。 */
function resolveStrategy(config: PublicConfig): VerifyStrategy {
  return config.verifyStrategy ?? (config.requireChallenge === false ? 'auto' : 'challenge');
}

const DEFAULT_CONFIG: PublicConfig = {
  requireChallenge: true,
  verifyStrategy: 'challenge',
  challengeTimeoutMs: 8000,
  template: DEFAULT_TEMPLATE,
  appName: '',
  logo: '',
  buttonColor: '',
  keepInAppBrowser: false,
};
const PASSIVE_COLLECT_MS = 300;
/** 单次尝试的硬超时。worker 侧 I/O 已有上界,两次尝试的最坏值仍是原先的 8s。 */
const VERIFY_TIMEOUT_MS = 4_000;
/** 失败自动再试一次:瞬时抖动不该让访客自己去点重试。 */
const VERIFY_MAX_ATTEMPTS = 2;

export interface UseCloakResult {
  state: CloakState;
  onAccept: (kind: 'click' | 'touch') => void;
  template: string;
  appName: string;
  logo: string;
  buttonColor: string;
}

export function useCloak(): UseCloakResult {
  const [state, setState] = useState<CloakState>({ status: 'loading' });
  const injectedConfig = typeof window !== 'undefined' ? window.__TC_CONFIG : undefined;
  const [config, setConfig] = useState<PublicConfig | null>(injectedConfig ?? null);
  const linkId = getLinkId();
  // runVerify 的闭包只依赖 linkId(见其 deps),config 后到不会重建它 —— 用 ref 镜像
  // 开关,注入路径首帧就是终值,fetch 兜底路径在 setConfig 处同步更新。
  const keepInAppRef = useRef(injectedConfig?.keepInAppBrowser === true);
  const verifyDoneRef = useRef(false);
  const verifyResultRef = useRef<VerifyResponse | null>(null);
  // preheat 场景也复用它:用户在预热 verify 在途时点击即置位,预热完成据此消费跳转。
  const challengeDoneRef = useRef(false);

  const runVerify = useCallback(
    async (trigger: 'auto' | 'challenge' | 'preheat' = 'auto') => {
      if (verifyDoneRef.current) return;
      verifyDoneRef.current = true;
      slDbg(`runVerify start trigger=${trigger}`);
      try {
        // preheat 静默预热:不改 UI(按钮保持可见),结果存 ref 供点击瞬时消费。
        if (trigger !== 'preheat') setState({ status: 'verifying' });
        // auto/preheat 无真实点击挑战:按已通过记录,避免 challengeFailed 误命中 ——
        // preheat 的人机门槛由"点击才跳"在前端保留,不依赖引擎 challenge 信号。
        if (trigger !== 'challenge') recordChallenge({ passed: true, ms: 0, kind: 'none' });

        trackEvent('fingerprintStart');
        const signals = await collectSignals();
        trackEvent('fingerprintDone');

        const body: VerifyRequest = {
          sessionId,
          deviceId: getDeviceId(),
          linkId,
          ...getAdClickIds(),
          referer: document.referrer || undefined,
          pageHref: window.location.href,
          version: getAppVersion(),
          signals,
          events: getEvents(),
          // 签名令牌带回 worker 验签做无状态交叉校验;缺失时引擎按信号降级。
          edgeToken: typeof window.__TC_EDGE === 'string' ? window.__TC_EDGE : undefined,
          probeToken: getProbeResult()?.probeToken,
        };
        // 重试只重发请求:signals 重采一遍要多花约 2.5s,结果还逐字节相同。
        // 只对网络失败/超时重试 —— HTTP 错误码是服务端的确定答复,重发改变不了。
        let res: Response | undefined;
        let lastError: unknown;
        for (let attempt = 1; attempt <= VERIFY_MAX_ATTEMPTS; attempt++) {
          trackEvent('verifyRequested', { attempt });
          // events 快照要重取,否则本次的 verifyRequested 进不了请求体,
          // 却会被成功后的 markAllEventsFlushed 标记成已发送 —— 埋点凭空丢失。
          body.events = getEvents();
          // fetch 硬超时:防请求在某些 in-app webview 里挂起不返回。用 AbortController
          // (比 AbortSignal.timeout 兼容更老的 iOS Safari)。
          const ctrl = new AbortController();
          const fetchTimeout = setTimeout(() => ctrl.abort(), VERIFY_TIMEOUT_MS);
          try {
            res = await postTelemetryJson('/api/verify', body, ctrl.signal);
            break;
          } catch (e) {
            lastError = e;
            slDbg(`verify attempt=${attempt} network fail ${e instanceof Error ? e.message : e}`);
          } finally {
            clearTimeout(fetchTimeout);
          }
        }
        if (!res) throw lastError ?? new Error('verify unreachable');
        markAllEventsFlushed();
        if (!res.ok) {
          // 把 400 校验错误体(含失败字段路径)带进 ?dbg 浮层,便于在 in-app webview
          // 里定位是哪个字段过不了 schema(看不到 console / network)。
          const errBody = await res.text().catch(() => '');
          slDbg(`verify ${res.status} FAIL ${errBody.slice(0, 300)}`);
          throw new Error(`verify failed: ${res.status}`);
        }
        const data = (await res.json()) as VerifyResponse;
        markVerifyDone();
        verifyResultRef.current = data;
        (window as unknown as { __TC_VR?: unknown }).__TC_VR = data;
        slDbg(
          `verify ${res.status} decision=${data.decision} redirect=${data.redirect || '(EMPTY)'}`,
        );

        // 空 redirect（block 且 redirectOnBlock=false）：原地停留在模板页，
        // 不发起任何跳转/外部打开 —— 被拦访客连 decoy URL 都没有。
        // preheat 预热完成:若用户已在预热期间点击(challengeDoneRef),立即消费跳转
        // (瞬时);否则静默停在 awaiting-click 等点击(cloak 语义:点击才跳)。
        if (trigger === 'preheat') {
          if (challengeDoneRef.current && data.redirect)
            redirectToTarget(data.redirect, keepInAppRef.current);
          return;
        }

        if (trigger === 'challenge') {
          setState({ status: 'awaiting-click', decision: data.decision });
          if (data.redirect) redirectToTarget(data.redirect, keepInAppRef.current);
          return;
        }

        // auto: pass 有 redirect 直跳,block(空 redirect)停留模板页。
        if (!data.redirect) {
          setState({ status: 'awaiting-click', decision: data.decision });
          return;
        }

        // keepInAppBrowser 关掉 breakout 后,这里走 else 分支的 inline replace ——
        // 与 redirectToTarget 的内联跳转同义,状态也如实标成 redirecting。
        const breakout = willBreakOut(keepInAppRef.current);
        if (breakout) {
          redirectToTarget(data.redirect, keepInAppRef.current);
          setState({ status: 'awaiting-click', decision: data.decision });
        } else {
          setState({ status: 'redirecting', decision: data.decision });
          window.location.replace(data.redirect);
        }
      } catch (e) {
        slDbg(`runVerify ERROR ${e instanceof Error ? e.message : String(e)}`);
        // 两个闸门都要复位,否则点击走不到重发分支;状态也必须回到 awaiting-click ——
        // 模板把其余状态一律渲染成不可点的 CHECKING,那正是 2026-08-17 的卡死态。
        verifyDoneRef.current = false;
        challengeDoneRef.current = false;
        setState({ status: 'awaiting-click' });
      }
    },
    [linkId],
  );

  useEffect(() => {
    startProbe();
  }, []);

  useEffect(() => {
    // The edge already injected the full config (window.__TC_CONFIG); only fetch
    // as a fallback when it's absent (e.g. dev server without the Pages middleware).
    if (injectedConfig) return;
    let cancelled = false;
    const params = linkId ? `?linkId=${encodeURIComponent(linkId)}` : '';
    fetch(`${getApiBase()}/api/config${params}`)
      .then((r) => (r.ok ? r.json() : DEFAULT_CONFIG))
      .catch(() => DEFAULT_CONFIG)
      .then((cfg: PublicConfig) => {
        if (cancelled) return;
        keepInAppRef.current = cfg.keepInAppBrowser === true;
        setConfig(cfg);
      });
    return () => {
      cancelled = true;
    };
  }, [linkId, injectedConfig]);

  useEffect(() => {
    if (!config) return;
    if (config.linkStatus === 'paused') {
      setState({ status: 'error', message: 'link unavailable' });
      return;
    }
    const strategy = resolveStrategy(config);
    challengeDoneRef.current = false;
    const verifyTimer = setTimeout(() => {
      if (strategy === 'auto') {
        void runVerify('auto');
        return;
      }
      // challenge: 显示挑战按钮 + 后台预热 verify(结果存 ref),点击时瞬时消费,把
      // 2.88s 藏到点击前 —— 预热是 challenge 的固有优化,cloak 语义不变(仍点击才跳)。
      trackEvent('challengeShown');
      setState({ status: 'awaiting-click' });
      void runVerify('preheat');
    }, PASSIVE_COLLECT_MS);
    const timeoutTimer = setTimeout(() => {
      // 点击已处理过则不重复带走。
      if (challengeDoneRef.current) return;
      // challenge 已在 verifyTimer 预热、auto 已自动 verify;超时兜底消费已就绪结果。
      // 未就绪(预热在途)则等自然完成或点击 —— 不强行带走。
      const data = verifyResultRef.current;
      if (!data) return;
      trackEvent('challengeTimeout', { ms: config.challengeTimeoutMs });
      void flushEventsBeacon();
      if (strategy === 'challenge') {
        // 对齐点击后的跳转方式:唤起系统浏览器(而非 inline replace);
        // keepInAppBrowser 链接则同样内联跳转,两条路径始终一致。
        if (data.redirect) redirectToTarget(data.redirect, config.keepInAppBrowser === true);
        return;
      }
      // auto: In an in-app webview the native "leave app" dialog is showing as an
      // overlay; auto-replacing the current page yanks the visitor away from
      // their own decision. Stay put — they break out by confirming the dialog
      // or re-tapping the CTA. Only force an inline redirect when we are NOT
      // relying on a scheme breakout. (Matches openInSystemBrowser's design.)
      // Empty redirect (redirectOnBlock=false): stay put unconditionally.
      if (data.redirect && !willBreakOut(config.keepInAppBrowser === true))
        window.location.replace(data.redirect);
    }, config.challengeTimeoutMs);
    return () => {
      clearTimeout(verifyTimer);
      clearTimeout(timeoutTimer);
    };
  }, [config, runVerify]);

  const onAccept = useCallback(
    (kind: 'click' | 'touch') => {
      const data = verifyResultRef.current;
      slDbg(
        `onAccept kind=${kind} hasResult=${!!data} redirect=${data?.redirect || '(none/notready)'}`,
      );
      if (data) {
        // 结果已就绪(preheat 预热完成 / 已 verify):点击瞬时消费。
        // 空 redirect（redirectOnBlock=false）：按钮点击无副作用，停留原页。
        if (data.redirect) redirectToTarget(data.redirect, keepInAppRef.current);
        return;
      }
      if (challengeDoneRef.current) return;
      challengeDoneRef.current = true;
      const ms = Math.round(performance.now());
      recordChallenge({ passed: true, ms, kind });
      trackEvent(kind === 'touch' ? 'challengeTouched' : 'challengeClicked', { ms });
      // challenge 档尚未 verify:现在发起。preheat 在途(verify 已发起、结果未回):不重发,
      // challengeDoneRef 已置位,预热完成分支据此消费跳转。
      if (!verifyDoneRef.current) void runVerify('challenge');
    },
    [runVerify],
  );

  return {
    state,
    onAccept,
    template: config?.template ?? DEFAULT_TEMPLATE,
    appName: config?.appName ?? '',
    logo: config?.logo ?? '',
    buttonColor: config?.buttonColor ?? '',
  };
}
